import axios from '../../api/axios';
import {
  CLIENTS_ENDPOINT,
  CONTRACTS_ENDPOINT,
  EQUIPMENTS_ENDPOINT,
  JOBS_ENDPOINT,
} from '../../consts';

export type TutorialPlacement = 'top' | 'bottom' | 'left' | 'right';

export interface TutorialStep {
  id: string;
  /** i18n keys. */
  title: string;
  body: string;
  /**
   * Where the step happens: a path, or one worked out from the demo's own
   * records. A record that cannot be found (deleted while trying the demo)
   * skips the step rather than stopping the tutorial.
   */
  route?: string | (() => Promise<string | null>);
  /** What to spotlight - the first selector with something visible on screen
   *  wins; none (or none visible, e.g. the toolbar on a narrow screen) puts
   *  the card in the middle. */
  target?: string[];
  placement?: TutorialPlacement;
  /** Room around the spotlight; 0 for something flush with the screen edge,
   *  like the side menu, whose own outline is the natural cut. */
  spotlightPadding?: number;
  /** Shown only to someone with at least one of these... */
  permissions?: string[];
  /** ...or, to anyone else, as this description (i18n key) with nothing
   *  opened - for screens worth knowing about even before one can see them. */
  withoutPermission?: string;
}

export interface TutorialSection {
  id: string;
  /** i18n key. */
  title: string;
  steps: TutorialStep[];
}

const PAGE = '[data-tour="page"]';
const PAGE_TABS = `${PAGE} .MuiTabs-root`;
const TOOLBAR = '[data-tour="toolbar"]';
const toolbarAction = (id: string) => `[data-tour="toolbar"] [data-tour-action="${id}"]`;

/**
 * The path of the first record a list endpoint returns for `params`.
 *
 * The records are the demo's (backend/app/demo/blueprint.py) - renaming one
 * there means updating the search here, or the step it belongs to drops out.
 */
const findRecord = async (
  endpoint: string,
  params: Record<string, string>,
  toPath: (id: number | string) => string
): Promise<string | null> => {
  try {
    const { data } = await axios.get(endpoint, { params: { ...params, page_size: 1 } });
    const record = data?.results?.[0];
    return record ? toPath(record.id) : null;
  } catch {
    return null;
  }
};

export const TUTORIAL_SECTIONS: TutorialSection[] = [
  {
    id: 'welcome',
    title: 'Welcome',
    steps: [
      {
        id: 'welcome',
        title: 'Welcome to Copigo',
        body: 'Your workspace now holds a made-up copier service with over a year of history. In a few short sections we will show you where everything is. Go back whenever you like, skip a section you do not need, or end the tutorial at any time.',
        route: '/home',
      },
    ],
  },
  {
    id: 'basics',
    title: 'Getting around',
    steps: [
      {
        id: 'main-menu',
        title: 'Main menu',
        body: 'Service, equipment, clients, settlements, contracts and invoices - hover over an icon to see its name.',
        route: '/home',
        target: ['[data-tour="main-menu"]'],
        placement: 'right',
        spotlightPadding: 0,
      },
      {
        id: 'dashboard',
        title: 'Dashboard',
        body: 'The day at a glance: service jobs by status, and below them this month’s settlements and contracts. A tile opens the list behind its number.',
        route: '/home',
        target: ['[data-tour="dashboard-jobs"]', PAGE],
        placement: 'bottom',
      },
      {
        id: 'toolbar',
        title: 'Actions',
        body: 'What you can do on a screen is always on the right and changes with the screen - here a new job, the settlement creator and the status of reading counters from e-mails.',
        route: '/home',
        target: [TOOLBAR],
        placement: 'left',
      },
      {
        id: 'notifications',
        title: 'Notifications',
        body: 'The bell tells you what happened in the meantime: a job changed status, a part’s stock in the warehouse fell to its notification threshold, KSeF refused an invoice.',
        route: '/home',
        target: ['[data-tour="notifications"]'],
        placement: 'bottom',
        permissions: ['messaging.view_notifications'],
      },
    ],
  },
  {
    id: 'clients',
    title: 'Clients and equipment',
    steps: [
      {
        id: 'clients',
        title: 'Clients',
        body: 'The companies and people you work for. Search by name, NIP or address - including the addresses where their devices stand.',
        route: '/clients',
        target: [PAGE],
        permissions: ['settlement.view_client'],
      },
      {
        id: 'client',
        title: 'Everything about a client in one place',
        body: 'The tabs gather the client’s contracts, monthly settlements, equipment, who can sign in to the client portal, and invoice defaults.',
        route: () =>
          findRecord(CLIENTS_ENDPOINT, { search: 'Wróblewski' }, (id) => `/clients/${id}`),
        target: [PAGE_TABS, PAGE],
        placement: 'bottom',
        permissions: ['settlement.view_client'],
      },
      {
        id: 'counters',
        title: 'Counter readings',
        body: 'Every device keeps the history of its counters, with where each reading came from: typed in by your team, sent by the client in the portal, or read from an e-mail report or a PDF.',
        route: () =>
          findRecord(
            EQUIPMENTS_ENDPOINT,
            { search: 'KM-C450i-88231' },
            (id) => `/equipment-section/equipments/${id}/counters`
          ),
        target: [PAGE],
        permissions: ['settlement.view_equipment'],
      },
    ],
  },
  {
    id: 'contracts',
    title: 'Contracts',
    steps: [
      {
        id: 'contracts',
        title: 'Contracts',
        body: 'For each contract: how many devices it covers, how they are billed, and whether this month is settled yet.',
        route: '/contracts',
        target: [PAGE],
        permissions: ['settlement.view_contract'],
      },
      {
        id: 'automation-level',
        title: 'How far automation goes',
        body: 'Prices, fees and page limits are set per device. The automation level decides what Copigo does by itself: confirm settlements, issue invoices, file them in KSeF and e-mail them to the client - this contract goes all the way.',
        route: () =>
          findRecord(CONTRACTS_ENDPOINT, { search: 'Mokotowska' }, (id) => `/contracts/${id}`),
        target: [toolbarAction('set-automation-level'), PAGE],
        placement: 'left',
        permissions: ['settlement.view_contract'],
      },
    ],
  },
  {
    id: 'settlements',
    title: 'Settlements',
    steps: [
      {
        id: 'sale-summary',
        title: 'Monthly settlements',
        body: 'Every month Copigo raises the settlements from counter readings. Here they are for the month you pick, with amounts, pages and the invoice issued for them.',
        route: '/settlements',
        target: [PAGE],
        permissions: ['settlement.view_sale_summary'],
      },
      {
        id: 'settlement-creator',
        title: 'Settlement creator',
        body: 'It takes you through the settlements still waiting - for a missing reading or for your confirmation - one after another.',
        route: '/settlements',
        target: [toolbarAction('settlement-creator'), TOOLBAR],
        placement: 'left',
        permissions: ['settlement.view_sale_summary'],
      },
    ],
  },
  {
    id: 'invoices',
    title: 'Invoices',
    steps: [
      {
        id: 'invoices',
        title: 'Invoices',
        body: 'Raised from settlements and service jobs, by hand or automatically. The KSeF column shows whether an invoice reached the National e-Invoice System - in the demo nothing is really sent.',
        route: '/invoices',
        target: [PAGE],
        permissions: ['invoice.view_invoice'],
      },
    ],
  },
  {
    id: 'service',
    title: 'Service',
    steps: [
      {
        id: 'jobs',
        title: 'Service jobs',
        body: 'From the report, through the visit and its verification, to the end. Clients can also report a fault themselves in the client portal.',
        route: '/service/jobs',
        target: [PAGE],
        permissions: ['service.view_job'],
      },
      {
        id: 'job',
        title: 'A job',
        body: 'Tasks per device, the parts and services used, the history of changes and messages within the team. Moving it on checks what the next status needs - counters or a description, for instance.',
        route: () =>
          findRecord(JOBS_ENDPOINT, { status: 'FV' }, (id) => `/service/jobs/${id}`),
        target: [PAGE],
        permissions: ['service.view_job'],
      },
      {
        id: 'stock',
        title: 'Parts warehouse',
        body: 'Stock levels and orders at their purchase prices, with a warning when a part runs low.',
        route: '/service/stock-status',
        target: [PAGE],
        permissions: ['service.view_part'],
      },
    ],
  },
  {
    id: 'statistics',
    title: 'Statistics and profitability',
    steps: [
      {
        id: 'service-statistics',
        title: 'Service statistics',
        body: 'How many jobs there were in a period, which devices need a technician most often and cost the most in parts, the most frequent faults and services - also per technician and per group of devices.',
        route: '/service/statistics/general-stats',
        target: [PAGE],
        permissions: ['service.view_job'],
      },
      {
        id: 'settlement-statistics',
        title: 'Settlement statistics',
        body: 'Monthly totals of confirmed settlements, pages used against the limits, and how they change from month to month.',
        route: '/settlements/statistics',
        target: [PAGE],
        permissions: ['settlement.can_view_settlement_statistics'],
        withoutPermission: 'Settlement statistics show the monthly totals of confirmed settlements and the pages used against the limits. Only people allowed to see them can open this screen - by default the Admin-Finanse group.',
      },
      {
        id: 'profitability',
        title: 'Profitability',
        body: 'Which devices, contracts and clients make money once the cost of pages and service visits is taken off.',
        route: '/settlements/profitability',
        target: [PAGE],
        permissions: ['settlement.can_view_profitability'],
        withoutPermission: 'Profitability shows which devices, contracts and clients make money once the cost of pages and service visits is taken off. Only people allowed to see it can open this screen - by default the Admin-Finanse group.',
      },
    ],
  },
  {
    id: 'settings',
    title: 'Settings',
    steps: [
      {
        id: 'users',
        title: 'Invite your team',
        body: 'Add technicians, the accounts department and anyone else, and decide what each of them may do. Invitations go out as usual, demo or not.',
        route: '/tenant/users',
        target: [toolbarAction('add-user'), PAGE],
        placement: 'left',
        permissions: ['users.view_user'],
      },
      {
        id: 'automation',
        title: 'Automation',
        body: 'The day of the month on which Copigo confirms settlements, issues the invoices and sends them - for the contracts that allow it.',
        route: '/tenant/automation',
        target: [PAGE],
        permissions: ['settlement.view_contract'],
      },
      {
        id: 'reminders',
        title: 'Reminders',
        body: 'Copigo asks clients for missing counter readings and reminds them of obligatory services. Here you set the wording of those e-mails, how often a client may be reminded and your own mail server to send them from.',
        route: '/tenant/settings/reminders',
        target: [PAGE],
        permissions: ['config.view_tenantsettings'],
      },
      {
        id: 'courier',
        title: 'Courier shipments',
        body: 'Connect your own Furgonetka.pl account and send parts from the warehouse by courier straight from Copigo, with the label and tracking. The connection is switched off while the demo data is in.',
        route: '/tenant/settings/integrations',
        target: [PAGE],
        permissions: ['config.view_tenantsettings'],
      },
    ],
  },
  {
    id: 'finish',
    title: 'Finally',
    steps: [
      {
        id: 'demo-data',
        title: 'Demo data',
        body: 'When you want to work on your own data, clear the demo data here - the workspace goes back to how it was before the demo. This is also where you can start the tutorial again.',
        route: '/tenant',
        target: ['[data-tour="demo-section"]', PAGE],
        placement: 'top',
      },
      {
        id: 'help',
        title: 'Over to you',
        body: 'Look around and click freely - nothing leaves the demo. When you are ready, clear the demo data, and if you need help moving your data from your current system, write to us with this button.',
        route: '/tenant',
        target: ['[data-tour="help"]'],
        placement: 'bottom',
      },
    ],
  },
];
